import clsx from "clsx";
import {
  MdAddCard,
  MdOutlineCreditCardOff,
  MdOutlineCreditScore,
  MdToggleOff,
  MdTune,
} from "react-icons/md";
import { ICON_SIZES } from "../../helpers/constants";
import { useNavigate } from "react-router-dom";
import useCurrentUser from "../../hooks/useCurrentUser";
import useMainFilter from "../../hooks/useMainFilter";
import { useTranslation } from "react-i18next";
import useMobile from "../../hooks/useMobile";
import AccountsListVertical from "../AccountsListVertical/AccountsListVertical";
import AccountNotFoundTextBlock from "./AccountNotFoundTextBlock/AccountNotFoundTextBlock";
import { motion } from "framer-motion";
import { EButtonKind } from "../Button/ButtonTypes";
import { memo } from "react";
import LoadingWithTextPage from "../LoadingWithText/LoadingWithTextPage";

interface IAccountNotFound {
  disabled?: boolean;
  exists?: boolean;
  linkTo:
    | "reports"
    | "transactions"
    | "transaction"
    | "recycle"
    | "attachments"
    | "recurring";
}

const AccountNotFound: React.FC<IAccountNotFound> = ({
  disabled,
  exists,
  linkTo,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    enabledAccountsList,
    hasRegisteredAccounts,
    isLoadingAccounts,
    allUserAccounts,
    userInfo,
  } = useCurrentUser();
  const { mainFilter } = useMainFilter();
  const { isMobile } = useMobile();

  const [ADD_ACCOUNT, ACCOUNTS_SETTINGS] = [
    {
      icon: <MdAddCard size={ICON_SIZES.MD} />,
      text: t("Agregar"),
      onClick: () => {
        navigate(`/settings/accounts/new`);
      },
      kind: EButtonKind.SAVE,
    },
    {
      icon: <MdTune size={ICON_SIZES.MD} />,
      text: t("Ajustes"),
      onClick: () => {
        navigate(`/settings/accounts/`);
      },
    },
  ];

  if (isLoadingAccounts) {
    return <LoadingWithTextPage />;
  }

  return (
    <div
      className={clsx([
        "flex",
        "w-full",
        "items-center",
        "justify-center",
        "flex-col",
        "gap-6",
        "md:gap-16",
        // "my-6",
        // "md:my-6",
        "h-full",
        "md:max-w-[800px]",
        "md:mx-auto",
        "-mt-[60px]",
        "md:mt-6",
      ])}
    >
      <div
        className={clsx([
          "grid",
          "grid-cols-1",
          "gap-8",
          "md:gap-14",
          "w-full",
          {
            "md:grid-cols-1":
              hasRegisteredAccounts && enabledAccountsList?.length,
          },
        ])}
      >
        <motion.div
          className={clsx([
            "flex",
            "flex-col",
            "items-center",
            "justify-center",
            "gap-2",
            "md:gap-12",
          ])}
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
            transition: {
              duration: 0.8,
            },
          }}
        >
          {hasRegisteredAccounts ? (
            <>
              {enabledAccountsList?.length ? (
                <>
                  {mainFilter.account ? (
                    <>
                      {exists ? (
                        <AccountNotFoundTextBlock
                          title={t("not_found_account.disabled_account_title")}
                          description={t(
                            "not_found_account.disabled_account_description"
                          )}
                          icon={
                            <MdToggleOff
                              size={
                                isMobile ? ICON_SIZES.XL : ICON_SIZES.XL * 1.5
                              }
                              className={clsx(["text-red-400"])}
                            />
                          }
                          buttons={[ADD_ACCOUNT, ACCOUNTS_SETTINGS]}
                        />
                      ) : (
                        <AccountNotFoundTextBlock
                          title={t("not_found_account.not_found_account_title")}
                          description={t(
                            "not_found_account.not_found_account_description"
                          )}
                          icon={
                            <MdOutlineCreditCardOff
                              size={
                                isMobile ? ICON_SIZES.XL : ICON_SIZES.XL * 1.5
                              }
                              className={clsx(["text-red-400"])}
                            />
                          }
                          buttons={[ADD_ACCOUNT, ACCOUNTS_SETTINGS]}
                        />
                      )}
                    </>
                  ) : (
                    <AccountNotFoundTextBlock
                      title={t("not_found_account.select_account_title")}
                      description={t(
                        "not_found_account.select_account_description"
                      )}
                      icon={
                        <MdOutlineCreditScore
                          size={isMobile ? ICON_SIZES.XL : ICON_SIZES.XL * 1.5}
                          className={clsx([
                            "text-red-400",
                            "dark:text-lime-400",
                          ])}
                        />
                      }
                      buttons={[ADD_ACCOUNT, ACCOUNTS_SETTINGS]}
                    />
                  )}
                </>
              ) : (
                <AccountNotFoundTextBlock
                  title={t("not_found_account.all_accounts_disabled_title", {
                    count: allUserAccounts?.length,
                  })}
                  description={t(
                    "not_found_account.all_accounts_disabled_description",
                    {
                      count: allUserAccounts?.length,
                    }
                  )}
                  icon={
                    <MdToggleOff
                      size={isMobile ? ICON_SIZES.XL : ICON_SIZES.XL * 1.5}
                      className={clsx(["text-red-400"])}
                    />
                  }
                  buttons={[ADD_ACCOUNT, ACCOUNTS_SETTINGS]}
                />
              )}
            </>
          ) : (
            <AccountNotFoundTextBlock
              title={t("not_found_account.no_accounts_title")}
              description={t("not_found_account.no_accounts_description")}
              icon={
                <MdOutlineCreditCardOff
                  size={isMobile ? ICON_SIZES.XL : ICON_SIZES.XL * 1.5}
                  className={clsx(["text-red-400"])}
                />
              }
              buttons={[ADD_ACCOUNT, ACCOUNTS_SETTINGS]}
            />
          )}
        </motion.div>
        {[hasRegisteredAccounts, !!enabledAccountsList?.length].includes(
          true
        ) && <AccountsListVertical linkTo={linkTo} />}
      </div>
    </div>
  );
};

export default memo(AccountNotFound);
